import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  DollarSign,
  CalendarIcon,
  Download,
  TrendingUp,
  CreditCard,
  Receipt,
  Filter,
  RefreshCw,
  FileSpreadsheet,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import { cn } from "../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mot de passe pour accéder à la section Finances
const FINANCES_PASSWORD = "Finances2025";

const FinancesPage = ({ user, settings }) => {
  const navigate = useNavigate();
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterActive, setFilterActive] = useState(false);

  // Check if already unlocked in session
  useEffect(() => {
    const unlocked = sessionStorage.getItem("finances_unlocked");
    if (unlocked === "true") {
      setIsUnlocked(true);
    }
  }, []);

  useEffect(() => {
    if (isUnlocked) {
      fetchData();
    }
  }, [isUnlocked]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === FINANCES_PASSWORD) {
      setIsUnlocked(true);
      sessionStorage.setItem("finances_unlocked", "true");
      setPasswordError("");
      toast.success("Accès autorisé");
    } else {
      setPasswordError("Mot de passe incorrect");
      toast.error("Mot de passe incorrect");
    }
  };

  const fetchData = async (start = null, end = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (start) params.append("start_date", start);
      if (end) params.append("end_date", end);
      
      const queryString = params.toString() ? `?${params.toString()}` : "";
      
      const [summaryRes, detailsRes] = await Promise.all([
        axios.get(`${API}/finances/summary${queryString}`, { withCredentials: true }),
        axios.get(`${API}/finances/details${queryString}`, { withCredentials: true })
      ]);
      
      setSummary(summaryRes.data);
      setDetails(detailsRes.data);
    } catch (error) {
      console.error("Error fetching finances:", error);
      toast.error("Erreur lors du chargement des données financières");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    const start = startDate ? format(startDate, "yyyy-MM-dd") : null;
    const end = endDate ? format(endDate, "yyyy-MM-dd") : null;
    setFilterActive(start !== null || end !== null);
    fetchData(start, end);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterActive(false);
    fetchData();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-CA", {
      style: "currency",
      currency: "CAD",
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return format(new Date(dateStr), "dd MMM yyyy", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const handleClientClick = (clientId) => {
    // Navigate to client file page and pre-select the client
    navigate("/client-file", { state: { selectedClientId: clientId } });
  };

  const exportToExcel = () => {
    if (details.length === 0) {
      toast.error("Aucune donnée à exporter");
      return;
    }

    // Create CSV content (compatible with Excel)
    const headers = [
      "Code Client",
      "Nom du Client",
      "Date de Paiement",
      "Honoraires",
      "Montant Payé",
      "Solde",
      "Type de Paiement",
      "Mode de Règlement",
      "Payeur"
    ];

    const csvContent = [
      headers.join(";"),
      ...details.map(row => [
        row.code_client,
        `"${row.nom_client}"`,
        formatDate(row.date_paiement),
        row.honoraires.toFixed(2).replace(".", ","),
        row.montant_paye.toFixed(2).replace(".", ","),
        row.solde.toFixed(2).replace(".", ","),
        row.type_paiement,
        row.mode_reglement,
        row.personne_payeur
      ].join(";"))
    ].join("\n");

    // Add BOM for Excel UTF-8 compatibility
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    const periodStr = filterActive 
      ? `_${startDate ? format(startDate, "yyyyMMdd") : "debut"}_${endDate ? format(endDate, "yyyyMMdd") : "fin"}`
      : "_complet";
    
    link.setAttribute("href", url);
    link.setAttribute("download", `finances${periodStr}_${format(new Date(), "yyyyMMdd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Export Excel téléchargé avec succès");
  };

  // Password protection screen
  if (!isUnlocked) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="finances-locked">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Lock className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Section Finances</CardTitle>
              <CardDescription>
                Cette section est protégée. Veuillez entrer le mot de passe pour accéder aux données financières.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={passwordInput}
                      onChange={(e) => {
                        setPasswordInput(e.target.value);
                        setPasswordError("");
                      }}
                      placeholder="Entrez le mot de passe"
                      className={passwordError ? "border-red-500" : ""}
                      data-testid="finances-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {passwordError && (
                    <p className="text-sm text-red-500">{passwordError}</p>
                  )}
                </div>
                <Button type="submit" className="w-full" data-testid="finances-unlock-button">
                  <Lock className="h-4 w-4 mr-2" />
                  Déverrouiller
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading && !summary) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6" data-testid="finances-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Finances
            </h1>
            <p className="text-muted-foreground mt-1">
              Suivi des paiements encaissés
            </p>
          </div>
          <Button
            onClick={exportToExcel}
            disabled={details.length === 0}
            data-testid="export-excel-button"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Exporter Excel
          </Button>
        </div>

        {/* Period Filter */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Filter className="h-5 w-5 text-primary" />
              Filtrer par période
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[200px] justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                      data-testid="start-date-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "dd MMM yyyy", { locale: fr }) : "Sélectionner"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-[200px] justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                      data-testid="end-date-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "dd MMM yyyy", { locale: fr }) : "Sélectionner"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <Button onClick={handleFilter} data-testid="apply-filter-button">
                <Filter className="h-4 w-4 mr-2" />
                Appliquer
              </Button>

              {filterActive && (
                <Button variant="outline" onClick={handleReset} data-testid="reset-filter-button">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Réinitialiser
                </Button>
              )}
            </div>

            {filterActive && (
              <p className="text-sm text-muted-foreground mt-4">
                Période filtrée : {startDate ? format(startDate, "dd MMM yyyy", { locale: fr }) : "Début"} 
                {" → "}
                {endDate ? format(endDate, "dd MMM yyyy", { locale: fr }) : "Fin"}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-100">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Encaissé</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="total-encaisse">
                    {formatCurrency(summary?.total_encaisse)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-blue-100">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Honoraires</p>
                  <p className="text-2xl font-bold text-blue-600" data-testid="total-honoraires">
                    {formatCurrency(summary?.total_honoraires)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-orange-100">
                  <Receipt className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Solde à Recevoir</p>
                  <p className="text-2xl font-bold text-orange-600" data-testid="solde-total">
                    {formatCurrency(summary?.solde_total)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-purple-100">
                  <CreditCard className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nombre de Paiements</p>
                  <p className="text-2xl font-bold text-purple-600" data-testid="nombre-paiements">
                    {summary?.nombre_paiements || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment Mode Breakdown */}
        {summary?.par_mode && Object.keys(summary.par_mode).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Répartition par mode de paiement</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(summary.par_mode).map(([mode, data]) => (
                  <div
                    key={mode}
                    className="p-4 rounded-lg bg-muted/50 border"
                  >
                    <p className="font-medium text-foreground">{mode}</p>
                    <p className="text-2xl font-bold text-primary mt-1">
                      {formatCurrency(data.amount)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {data.count} paiement{data.count > 1 ? "s" : ""}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detailed List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Liste détaillée des paiements</span>
              <span className="text-sm font-normal text-muted-foreground">
                {details.length} paiement{details.length > 1 ? "s" : ""}
              </span>
            </CardTitle>
            <CardDescription>
              Cliquez sur "Exporter Excel" pour télécharger la liste complète
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {details.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                Aucun paiement trouvé pour cette période
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="finances-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pl-6">Code</th>
                      <th>Nom du Client</th>
                      <th>Date</th>
                      <th className="text-right">Honoraires</th>
                      <th className="text-right">Montant Payé</th>
                      <th className="text-right">Solde</th>
                      <th>Mode</th>
                      <th className="pr-6">Payeur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((payment, index) => (
                      <tr key={index} data-testid={`payment-row-${index}`}>
                        <td className="pl-6 font-mono text-sm font-medium text-primary">
                          {payment.code_client}
                        </td>
                        <td>
                          <button
                            onClick={() => handleClientClick(payment.client_id)}
                            className="font-medium text-primary hover:text-primary/80 hover:underline transition-colors text-left"
                            data-testid={`client-link-${index}`}
                          >
                            {payment.nom_client}
                          </button>
                        </td>
                        <td className="text-muted-foreground">
                          {formatDate(payment.date_paiement)}
                        </td>
                        <td className="text-right font-mono">
                          {formatCurrency(payment.honoraires)}
                        </td>
                        <td className="text-right font-mono text-green-600">
                          {formatCurrency(payment.montant_paye)}
                        </td>
                        <td className={cn(
                          "text-right font-mono",
                          payment.solde > 0 ? "text-orange-600" : "text-muted-foreground"
                        )}>
                          {formatCurrency(payment.solde)}
                        </td>
                        <td>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted">
                            {payment.mode_reglement}
                          </span>
                        </td>
                        <td className="pr-6 text-muted-foreground">
                          {payment.personne_payeur}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/50 font-medium">
                      <td colSpan={3} className="pl-6">TOTAL</td>
                      <td className="text-right font-mono">
                        {formatCurrency(summary?.total_honoraires)}
                      </td>
                      <td className="text-right font-mono text-green-600">
                        {formatCurrency(summary?.total_encaisse)}
                      </td>
                      <td className="text-right font-mono text-orange-600">
                        {formatCurrency(summary?.solde_total)}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default FinancesPage;
